/**
 * `data-screen-label="Agent"`, tab `channels` — **Caller protection**, with **Blocked numbers** (plan-029 WAPP-PROT;
 * design §40.5, §40.6, §40.13.1, §40.13.2, §40.19.1; plan-029 §2.12; D-2, D-5, D-6; ADR 0081, proposed).
 *
 * Drawn under the person-handoff and pass panels, in the same card grammar (`ch-card`, `ch-card-label`,
 * `ch-card-actions`, the `c-answer-*` selects). The panel is inside an existing screen, so it carries no
 * `data-screen-label` (design §40.13.1).
 *
 * ```
 * Caller protection                                                       [Not available yet]
 *   Limit calls from one number   [Off ▾] per hour   [Off ▾] per day       (a caller over it hears a line, unbilled)
 *     the website note: visitors are counted by their network
 *   [switch] Refuse withheld numbers                                       (some phone systems count as withheld)
 *   End the call if the caller says nothing within   [Off | 10 | 20 seconds]
 *   End conversations after   [Never ▾ | 5 … 120 minutes]
 *   [Save] [Discard]   problems, refusals, "Saved"
 * Blocked numbers  (./blocked-number-panel.tsx)
 * ```
 *
 * THE SETTING LIVES ON THE VOICEAGENT, BESIDE THE VERSION (D-2): a save applies from the next call, with no draft and
 * no publish; `If-Match` names the revision read and `409 revision_stale` reloads the setting and says so. While the
 * api's switch is off (`available: false`) the stored values show, nothing can change, and the panel says "Not
 * available yet".
 *
 * SILENT START OFFERS ONLY 10 AND 20 SECONDS (decision recorded for AP-3). The supervisor's presence check already ends
 * a silent call after about 24 seconds (two "are you still there?" asks of 8 s each after the first 8 s; design §40.6:
 * the setting is "only ever stricter than the presence check"), so 30 and 60 seconds — which the api accepts — would
 * never take effect. They are not offered; a VoiceAgent that already stores one (saved through the api) shows it as
 * the chosen value with a line saying it has no effect.
 *
 * NEVER A FULL NUMBER: the blocked numbers show their last three digits only (`./blocked-number-panel.tsx`).
 */
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { ApiError } from "@wetalk/api-client";
import { Button, Icon, Segmented, Tag } from "@wetalk/ui";
import { useState, type ReactNode } from "react";

import "../../new/answering.css";
import "../../../billing/billing.css";
import "./caller-protection-panel.css";

import { apiErrorText } from "../../../../api/api-error.js";
import { useGateway } from "../../../../api/provider.js";
import {
  CALLER_PROTECTION_QUERY_KEY,
  LONGEST_CONVERSATION_MINUTE,
  PartyRefusedError,
  useVoiceAgentCallerProtection,
  type CallerProtectionChange,
  type CallerProtectionView,
} from "../../../../api/slice/caller-protection.js";
import { portalText, usePortalTranslation } from "../../../../i18n/portal.js";
import { GLYPH } from "../../../../shell/glyph.js";
import { useMemberCan } from "../../../../shell/member-capability.js";
import { SwitchControl } from "../../../billing/switch-control.js";
import { BlockedNumberPanel } from "./blocked-number-panel.js";

export interface CallerProtectionPanelProps {
  /** The VoiceAgent whose settings the panel shows. */
  readonly voiceAgentId: string;
}

/** The per-hour limits offered (the api accepts 1–100): a stored value outside them is offered too. */
export const PER_HOUR_CHOICE = [1, 2, 3, 5, 10, 20, 50, 100] as const;
/** The per-day limits offered (the api accepts 1–500). */
export const PER_DAY_CHOICE = [1, 2, 3, 5, 10, 20, 50, 100, 200, 500] as const;
/**
 * The silent-start ends that take effect: shorter than the presence check's ~24 s. The api's 30 and 60 never fire (see
 * the header), so they are not offered.
 */
export const SILENT_START_EFFECTIVE_SECOND = [10, 20] as const;

function sameChange(left: CallerProtectionChange, right: CallerProtectionChange): boolean {
  return (
    left.perHour === right.perHour &&
    left.perDay === right.perDay &&
    left.withheldPartyRefusal === right.withheldPartyRefusal &&
    left.silentStartEndSecond === right.silentStartEndSecond &&
    left.longestConversationMinute === right.longestConversationMinute
  );
}

function changeOf(view: CallerProtectionView): CallerProtectionChange {
  return {
    perHour: view.perHour,
    perDay: view.perDay,
    withheldPartyRefusal: view.withheldPartyRefusal,
    silentStartEndSecond: view.silentStartEndSecond,
    longestConversationMinute: view.longestConversationMinute,
  };
}

/** The choices of a select: the offered values plus the stored one when it is none of them, ascending. */
function choiceWith(offered: readonly number[], stored: number | null): readonly number[] {
  return stored === null || offered.includes(stored) ? offered : [...offered, stored].sort((left, right) => left - right);
}

/** A rule the change breaks before it is sent (the api's own order), worded; `null` when it may be sent. */
export function callerProtectionProblemOf(change: CallerProtectionChange): string | null {
  if (change.perHour !== null && change.perDay !== null && change.perDay < change.perHour) {
    return portalText("callerProtection", "rule.per_day_below_per_hour");
  }
  return null;
}

/** A refused save in the portal's language: the rule's code when the api named one, else the api's own sentence. */
export function callerProtectionRefusalTextOf(error: unknown): string {
  if (error instanceof PartyRefusedError) {
    switch (error.detailCode) {
      case "per_hour_out_of_range":
        return portalText("callerProtection", "rule.per_hour_out_of_range");
      case "per_day_out_of_range":
        return portalText("callerProtection", "rule.per_day_out_of_range");
      case "per_day_below_per_hour":
        return portalText("callerProtection", "rule.per_day_below_per_hour");
      case "silent_start_not_offered":
        return portalText("callerProtection", "rule.silent_start_not_offered");
      case "longest_conversation_not_offered":
        return portalText("callerProtection", "rule.longest_conversation_not_offered");
      default:
        break;
    }
  }
  if (error instanceof ApiError && error.code === "caller_protection_unavailable") {
    return portalText("callerProtection", "rule.unavailable");
  }
  return apiErrorText(error, portalText("callerProtection", "panel.refused_unknown"));
}

export function CallerProtectionPanel({ voiceAgentId }: CallerProtectionPanelProps): ReactNode {
  usePortalTranslation("callerProtection");
  const gateway = useGateway();
  const queryClient = useQueryClient();
  const canEdit = useMemberCan().can("voice_agent.write");
  const setting = useVoiceAgentCallerProtection(voiceAgentId);
  const [edited, setEdited] = useState<CallerProtectionChange | null>(null);
  const [savedLine, setSavedLine] = useState<"saved" | "reloaded" | null>(null);
  const [refusal, setRefusal] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: (input: { readonly change: CallerProtectionChange; readonly stored: CallerProtectionView }) =>
      gateway.changeVoiceAgentCallerProtection(voiceAgentId, input.change, input.stored.revision),
    onSuccess: (next) => {
      queryClient.setQueryData(CALLER_PROTECTION_QUERY_KEY.callerProtection(voiceAgentId), next);
      setEdited(null);
      setRefusal(null);
      setSavedLine("saved");
    },
    onError: async (error) => {
      if (error instanceof ApiError && error.code === "revision_stale") {
        await queryClient.invalidateQueries({ queryKey: CALLER_PROTECTION_QUERY_KEY.callerProtection(voiceAgentId) });
        setEdited(null);
        setRefusal(null);
        setSavedLine("reloaded");
        return;
      }
      setRefusal(callerProtectionRefusalTextOf(error));
    },
  });

  const heading = <h2 className="ch-section-title">{portalText("callerProtection", "panel.title")}</h2>;

  if (setting.isError) {
    return (
      <>
        {heading}
        <p className="c-note c-note-critical" role="alert">
          {portalText("callerProtection", "panel.read_failed")}{" "}
          {apiErrorText(setting.error, portalText("callerProtection", "panel.reload"))}
        </p>
      </>
    );
  }
  if (setting.data === undefined) {
    return (
      <>
        {heading}
        <p className="c-note">{portalText("callerProtection", "panel.reading")}</p>
      </>
    );
  }

  const stored = setting.data;
  const base = changeOf(stored);
  const draft = edited ?? base;
  const changed = !sameChange(draft, base);
  const locked = !canEdit || !stored.available;
  const problem = changed ? callerProtectionProblemOf(draft) : null;
  const silentNoEffect =
    draft.silentStartEndSecond !== null && !(SILENT_START_EFFECTIVE_SECOND as readonly number[]).includes(draft.silentStartEndSecond);

  const change = (next: Partial<CallerProtectionChange>): void => {
    setSavedLine(null);
    setRefusal(null);
    setEdited({ ...draft, ...next });
  };

  const silentOption = choiceWith(SILENT_START_EFFECTIVE_SECOND, stored.silentStartEndSecond).map((second) => ({
    value: String(second),
    label: portalText("callerProtection", "silent_start.value", { value: second }),
  }));

  return (
    <>
      <div className="cp-head">
        {heading}
        {stored.available ? null : <Tag tone="neutral">{portalText("callerProtection", "panel.unavailable")}</Tag>}
      </div>
      <p className="ch-section-dek">{portalText("callerProtection", "panel.dek")}</p>

      <div className="ch-card cp" data-caller-protection={stored.available ? "available" : "unavailable"}>
        {stored.available ? null : (
          <p className="cp-note" role="note">
            <Icon glyph={GLYPH.info} size="sm" />
            <span>{portalText("callerProtection", "panel.unavailable_note")}</span>
          </p>
        )}

        <div className="cp-row">
          <p className="ch-card-label ch-card-label-flush" id="cp-limit-label">
            {portalText("callerProtection", "limit.label")}
          </p>
          <div className="cp-limit" role="group" aria-labelledby="cp-limit-label">
            <LimitSelect
              id="cp-per-hour"
              label={portalText("callerProtection", "limit.per_hour_label")}
              offered={PER_HOUR_CHOICE}
              value={draft.perHour}
              stored={stored.perHour}
              disabled={locked}
              word={(value) => portalText("callerProtection", "limit.per_hour", { value })}
              onChange={(perHour) => {
                change({ perHour });
              }}
            />
            <LimitSelect
              id="cp-per-day"
              label={portalText("callerProtection", "limit.per_day_label")}
              offered={PER_DAY_CHOICE}
              value={draft.perDay}
              stored={stored.perDay}
              disabled={locked}
              word={(value) => portalText("callerProtection", "limit.per_day", { value })}
              onChange={(perDay) => {
                change({ perDay });
              }}
            />
          </div>
          <span className="c-answer-note">{portalText("callerProtection", "limit.dek")}</span>
          <span className="c-answer-note">{portalText("callerProtection", "limit.web_note")}</span>
        </div>

        <div className="cp-row">
          <SwitchControl
            label={portalText("callerProtection", "withheld.label")}
            checked={draft.withheldPartyRefusal}
            disabled={locked}
            onChange={(withheldPartyRefusal) => {
              change({ withheldPartyRefusal });
            }}
          />
          <span className="c-answer-note">{portalText("callerProtection", "withheld.trunk_note")}</span>
        </div>

        <div className="cp-row">
          <p className="ch-card-label ch-card-label-flush">{portalText("callerProtection", "silent_start.label")}</p>
          {locked ? (
            <>
              <p className="cp-value">
                {draft.silentStartEndSecond === null
                  ? portalText("callerProtection", "silent_start.off")
                  : portalText("callerProtection", "silent_start.value", { value: draft.silentStartEndSecond })}
              </p>
            </>
          ) : (
            <Segmented
              name={`cp-silent-start-${voiceAgentId}`}
              legend={portalText("callerProtection", "silent_start.label")}
              options={[{ value: "off", label: portalText("callerProtection", "silent_start.off") }, ...silentOption]}
              value={draft.silentStartEndSecond === null ? "off" : String(draft.silentStartEndSecond)}
              onChange={(value) => {
                change({ silentStartEndSecond: value === "off" ? null : Number(value) });
              }}
            />
          )}
          <span className="c-answer-note" role={silentNoEffect ? "note" : undefined}>
            {silentNoEffect
              ? portalText("callerProtection", "silent_start.no_effect", { value: draft.silentStartEndSecond ?? 0 })
              : portalText("callerProtection", "silent_start.note")}
          </span>
        </div>

        <div className="cp-row">
          <label className="ch-card-label ch-card-label-flush" htmlFor="cp-longest">
            {portalText("callerProtection", "longest.label")}
          </label>
          <span className="c-answer-select cp-select">
            <select
              id="cp-longest"
              className="input"
              value={draft.longestConversationMinute === null ? "" : String(draft.longestConversationMinute)}
              disabled={locked}
              onChange={(event) => {
                const raw = event.currentTarget.value;
                change({ longestConversationMinute: raw === "" ? null : Number(raw) });
              }}
            >
              <option value="">{portalText("callerProtection", "longest.never")}</option>
              {choiceWith(LONGEST_CONVERSATION_MINUTE, stored.longestConversationMinute).map((minute) => (
                <option key={minute} value={String(minute)}>
                  {portalText("callerProtection", "longest.value", { value: minute })}
                </option>
              ))}
            </select>
            <Icon glyph={GLYPH.caretDown} size="sm" />
          </span>
          <span className="c-answer-note">{portalText("callerProtection", "longest.note")}</span>
        </div>

        {canEdit ? null : <p className="ch-card-foot">{portalText("callerProtection", "panel.read_only")}</p>}

        {changed || savedLine !== null || refusal !== null ? (
          <div className="ch-card-actions">
            {changed ? (
              <Button
                variant="primary"
                disabled={problem !== null || save.isPending || locked}
                onClick={() => {
                  setRefusal(null);
                  save.mutate({ change: draft, stored });
                }}
              >
                {save.isPending ? portalText("callerProtection", "panel.saving") : portalText("callerProtection", "panel.save")}
              </Button>
            ) : null}
            {changed ? (
              <Button
                variant="ghost"
                onClick={() => {
                  setEdited(null);
                  setRefusal(null);
                }}
              >
                {portalText("callerProtection", "panel.discard")}
              </Button>
            ) : null}
            {problem === null ? null : (
              <span className="c-note c-note-critical" role="alert">
                {problem}
              </span>
            )}
            {refusal === null ? null : (
              <span className="c-note c-note-critical" role="alert">
                {refusal}
              </span>
            )}
            {savedLine === null ? null : (
              <span className="ch-note" role="status">
                {savedLine === "saved"
                  ? portalText("callerProtection", "panel.saved")
                  : portalText("callerProtection", "panel.revision_stale")}
              </span>
            )}
          </div>
        ) : null}
      </div>

      <BlockedNumberPanel voiceAgentId={voiceAgentId} />
    </>
  );
}

/** One of the two per-caller limits: "Off" or a count, worded "{n} per hour" / "{n} per day". */
function LimitSelect({
  id,
  label,
  offered,
  value,
  stored,
  disabled,
  word,
  onChange,
}: {
  readonly id: string;
  readonly label: string;
  readonly offered: readonly number[];
  readonly value: number | null;
  readonly stored: number | null;
  readonly disabled: boolean;
  readonly word: (value: number) => string;
  readonly onChange: (value: number | null) => void;
}): ReactNode {
  usePortalTranslation("callerProtection");
  return (
    <div className="field cp-limit-field">
      <label htmlFor={id}>{label}</label>
      <span className="c-answer-select cp-select">
        <select
          id={id}
          className="input"
          value={value === null ? "" : String(value)}
          disabled={disabled}
          onChange={(event) => {
            const raw = event.currentTarget.value;
            onChange(raw === "" ? null : Number(raw));
          }}
        >
          <option value="">{portalText("callerProtection", "limit.off")}</option>
          {choiceWith(offered, stored).map((count) => (
            <option key={count} value={String(count)}>
              {word(count)}
            </option>
          ))}
        </select>
        <Icon glyph={GLYPH.caretDown} size="sm" />
      </span>
    </div>
  );
}
